import React from "react";
import { useTheme } from "react-jss";
import numeral from "numeral";
import classNames from "classnames";
import ScrollableContainer from "../shared/ScrollableContainer";
import useStyles from "./PaginatedTable.styles";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import { TableFooter } from "./TableFooter";
import InputBox from "../shared/InputBox";
import { CheckBoxIcon } from "../shared/CheckBoxIcon";

export interface TableColumn {
    name: string;
    searchable?: boolean;
    width?: number;
    filter?: string;
    translatedName?: string;
}

export interface TableRow {
    id: number;
    selected?: boolean;
    [columnName: string]: any;
}

export interface ColumnFilterProps {
    value?: string;
    onChange?(value: string): void;
}

export interface TableTexts {
    search: string;
    selectedItems: string;
    selectedItemsLimit: string;
    emptyTable: string;
    info: string;
    infoFiltered: string;
    lengthMenu: string;
}

function ColumnFilter(props: ColumnFilterProps) {
    const { value: _value, onChange } = props;
    const theme = useTheme();
    const classes = useStyles({ theme });
    const prevValue = React.useRef(_value);
    const [value, setValue] = React.useState(_value);
    const onChangeRef = React.useRef(onChange);

    React.useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    React.useEffect(() => {
        if (prevValue.current === value) return undefined;
        prevValue.current = value;
        const timer = setTimeout(() => onChangeRef.current && onChangeRef.current(value), 500);
        return () => {
            clearTimeout(timer);
        };
    }, [value]);

    return (
        <input
            type="text"
            className={classes.tableColumnFilterInput}
            value={value}
            onChange={(e) => setValue(e.target.value)}
        />
    );
}

interface PaginatedTableRowProps {
    index: number;
    row: TableRow;
    tableProps: PaginatedTableProps;
}

function PaginatedTableRow(props: PaginatedTableRowProps) {
    const { index, row, tableProps } = props;
    const { selectable, columns, max, totalSelected, onChangeRowSelected } = tableProps;
    const theme: any = useTheme();
    const classes = useStyles({ theme });
    const [hover, setHover] = React.useState(false);

    return (
        // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
        <tr
            id={String(row.id)}
            key={row.id}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
            className={classNames(
                classes.tableRow,
                index % 2 === 1 && classes.tableRowAlternate,
                selectable && !row.disabled && classes.tableRowSelectable,
                row.selected && classes.tableRowSelected,
            )}
            onClick={(e) => {
                if (row.selected || !max || totalSelected < max) {
                    onChangeRowSelected(row, !row.selected);
                    e.preventDefault();
                }
            }}
        >
            {selectable && theme.DisplayChoiceIcon === "left" && (
                <td className={classes.tableRowCheckbox}>
                    <CheckBoxIcon
                        checked={row.selected}
                        hover={hover}
                        disabled={!row.selected && max && totalSelected >= max}
                        backgroundless={theme.ControlAlpha === 0}
                    />
                </td>
            )}
            {columns.map(({ name, width, searchable }) => (
                <td
                    id={`${row.id}_${name}`}
                    key={name}
                    style={{ minWidth: width, maxWidth: width }}
                >
                    {row[name]}
                </td>
            ))}
            {selectable && theme.DisplayChoiceIcon === "right" && (
                <td className={classes.tableRowCheckbox}>
                    <CheckBoxIcon
                        checked={row.selected}
                        disabled={!row.selected && max && totalSelected >= max}
                        backgroundless={theme.ControlAlpha === 0}
                    />
                </td>
            )}
        </tr>
    );
}

export interface PaginatedTableProps {
    id: string;
    columns: TableColumn[];
    defaultRowsPerPage?: number;
    rowsPerPageOptions: number[];
    pageRows: TableRow[];
    pageStart: number;
    max?: number;
    selectable?: boolean;
    filterable?: boolean;
    sortColumn?: string;
    sortAsc?: boolean;
    totalRows: number;
    totalFilteredRows?: number;
    totalSelected?: number;
    search?: string;
    texts?: TableTexts;
    onChangeRowSelected?(row: TableRow, selected: boolean): void;
    onChangeSort?(sortColumn: string, sortAsc: boolean): void;
    onChangePage?(pageStart: number, length: number): void;
    onChangeSearch?(value: string): void;
    onChangeColumnFilter?(column: string, value: string): void;
}

export function PaginatedTable(props: PaginatedTableProps) {
    const {
        columns,
        pageRows,
        pageStart,
        max,
        totalRows,
        totalFilteredRows,
        totalSelected,
        sortAsc,
        sortColumn,
        selectable,
        filterable,
        onChangeSort,
        defaultRowsPerPage,
        rowsPerPageOptions,
        search: searchValue,
        texts,
        onChangeSearch,
        onChangeColumnFilter,
        onChangePage,
    } = props;
    const initialRowsPerPage = defaultRowsPerPage || rowsPerPageOptions[0] || 10;
    const prevRowsPerPage = React.useRef(initialRowsPerPage);
    const [rowsPerPage, setRowsPerPage] = React.useState(initialRowsPerPage);
    const onChangePageRef = React.useRef<(pageStart: number, length: number) => void>();

    const pageCount = Math.ceil(totalFilteredRows / rowsPerPage);
    const currentPage = Math.floor(pageStart / rowsPerPage) + 1;

    const prevSearch = React.useRef(searchValue);
    const [search, setSearch] = React.useState(searchValue);

    const theme: any = useTheme();
    const classes = useStyles({ theme });

    React.useEffect(() => {
        if (prevSearch.current === search) return undefined;
        prevSearch.current = search;
        const timer = setTimeout(() => onChangeSearch(search), 500);
        return () => {
            clearTimeout(timer);
        };
    }, [search, onChangeSearch]);

    React.useEffect(() => {
        onChangePageRef.current = onChangePage;
    }, [onChangePage]);

    React.useEffect(() => {
        if (prevRowsPerPage.current === rowsPerPage) return;
        prevRowsPerPage.current = rowsPerPage;
        onChangePageRef.current(0, rowsPerPage);
    }, [rowsPerPage]);

    const hasSearchableColumns = columns.some((c) => c.searchable);

    return (
        <div className={classes.paginatedTable}>
            {hasSearchableColumns && (
                <InputBox accessoryLeft="search" style={{ marginBottom: 8 }}>
                    <input
                        type="text"
                        placeholder={texts.search}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </InputBox>
            )}
            <br />
            <ScrollableContainer>
                <table className={classes.table}>
                    <thead>
                        <tr>
                            {selectable && theme.DisplayChoiceIcon === "left" && (
                                <th className={classes.tableColumn} />
                            )}
                            {columns.map(({ name, width, searchable, translatedName }) => (
                                <th
                                    key={name}
                                    onClick={() =>
                                        onChangeSort(name, sortColumn === name ? !sortAsc : true)
                                    }
                                    style={{ minWidth: width, maxWidth: width }}
                                    className={classNames(
                                        classes.tableColumn,
                                        sortColumn === name && classes.sortedColumn,
                                    )}
                                >
                                    {searchable && (
                                        <FontAwesomeIcon
                                            icon={[
                                                sortColumn === name && classes.sortedColumn
                                                    ? "fas"
                                                    : "far",
                                                "search",
                                            ]}
                                            size="sm"
                                            fixedWidth
                                        />
                                    )}
                                    {translatedName || name}
                                    {sortColumn === name && (
                                        <FontAwesomeIcon
                                            icon={sortAsc ? "angle-down" : "angle-up"}
                                            fixedWidth
                                        />
                                    )}
                                </th>
                            ))}
                            {selectable && theme.DisplayChoiceIcon === "right" && (
                                <th className={classes.tableColumn} />
                            )}
                        </tr>
                        {filterable && (
                            <tr>
                                {selectable && theme.DisplayChoiceIcon === "left" && (
                                    <th className={classes.tableColumn} />
                                )}
                                {columns.map(({ name, width, filter }) => (
                                    <th
                                        key={name}
                                        className={classes.tableColumnFilter}
                                        style={{ minWidth: width, maxWidth: width }}
                                    >
                                        <ColumnFilter
                                            value={filter || ""}
                                            onChange={(value) => onChangeColumnFilter(name, value)}
                                        />
                                    </th>
                                ))}
                                {selectable && theme.DisplayChoiceIcon === "right" && (
                                    <th className={classes.tableColumn} />
                                )}
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {pageRows.map((row, index) => (
                            <PaginatedTableRow
                                key={row.id}
                                index={index}
                                row={row}
                                tableProps={props}
                            />
                        ))}
                    </tbody>
                </table>
            </ScrollableContainer>
            <TableFooter
                tableProps={props}
                rowsPerPage={rowsPerPage}
                pageCount={pageCount}
                currentPage={currentPage}
                lengthMenuText={texts.lengthMenu}
                onChangeRowsPerPage={setRowsPerPage}
            >
                {!pageRows.length && texts.emptyTable}
                {pageRows.length > 0 &&
                    texts.info
                        .replace("_START_", `${pageStart + 1}`)
                        .replace("_END_", `${pageStart + pageRows.length}`)
                        .replace("_TOTAL_", numeral(totalFilteredRows).format("0,0"))}
                {totalFilteredRows < totalRows
                    ? ` ${texts.infoFiltered.replace("_MAX_", numeral(totalRows).format("0,0"))}`
                    : ""}
                {totalSelected
                    ? ` ${(max ? texts.selectedItemsLimit : texts.selectedItems)
                          .replace("{count}", `${totalSelected}`)
                          .replace("{limit}", `${max || ""}`)}`
                    : ""}
            </TableFooter>
        </div>
    );
}
